import SortArrows from '@/assets/icons/chevron-selector-vertical.svg?react';
import styles from './call-logs-table.module.scss';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { CallRow } from '@/services/call-log-service/call-log-service.types';
import { formatConversationDateTime } from '@/utils/helpers';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Divider, Empty, Flex, Spin, TablePaginationConfig } from 'antd';
import { ColumnsType, FilterValue, SorterResult } from 'antd/es/table/interface';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TopicChip from '../topic-chip/topic-chip';
import VsTable from '../vs-table/vs-table';
import StarRating from '../star-rating/star-rating';
import StatusBadge from '../status-badge/status-badge';
import { deriveStatusVariant } from '../status-badge/status-badge.utils';
import { scoreToFive } from '@/utils/score';
import {
  generateDataSource,
  renderCallControl,
  renderDuration,
} from './call-logs-table.utils';
import { Dispatch, SetStateAction } from 'react';

interface CallLogsTableProps {
  onChange: (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => void;
  onSelectRecord?: (record: CallRow) => void;
  selectedRecord?: CallRow;
  callLogData?: CallRow[];
  setIsPlaying?: Dispatch<SetStateAction<boolean>>;
  isPlaying?: boolean;

  loading?: boolean;
  loadingRecordId?: number;
  /** Multi-company admins only. The mockup is a single-tenant panel. */
  showCompany?: boolean;
  /** Off for a fixed five-row snapshot, where sorting the newest calls means little. */
  sortable?: boolean;
}

export const CallLogsTable = ({
  onChange,
  onSelectRecord,
  callLogData,
  selectedRecord,
  setIsPlaying,
  isPlaying = false,
  loading,
  loadingRecordId,
  showCompany = false,
  sortable = true,
}: CallLogsTableProps) => {
  const { t } = useTranslation('conversations');
  const navigate = useNavigate();
  const dataSource = generateDataSource(callLogData || []);

  // Both sortable columns share one affordance, so it is declared once.
  const sortProps = sortable
    ? {
        sorter: true,
        sortIcon: () => (
          <Icon
            component={() => <SortArrows stroke={styles.arrowColorIcon} width={14} height={14} />}
          />
        ),
      }
    : {};

  const handleChange = (
    pagination: TablePaginationConfig,
    _: Record<string, FilterValue | null>,
    sorter: SorterResult<CallRow> | SorterResult<CallRow>[]
  ) => {
    if (!Array.isArray(sorter)) {
      sorter = [sorter];
    }

    const sort = sorter.find((s) => s.order);

    const sortBy = sort?.field?.toString();
    const orderBy = sort ? (sort.order === 'ascend' ? 'asc' : 'desc') : undefined;

    onChange(pagination.current, pagination.pageSize, sortBy, orderBy);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: ColumnsType<CallRow> = [
    {
      // The mockup leaves this header empty, the control is the only content.
      title: '',
      dataIndex: 'id',
      key: 'play',
      className: styles.callContainer,
      render: (_: CallRow['id'], record) => (
        <div className={styles.aboveRowLink}>
          {renderCallControl(
            record,
            record.duration,
            selectedRecord,
            onSelectRecord,
            setIsPlaying,
            loadingRecordId,
            isPlaying
          )}
        </div>
      ),
    },
    {
      title: t('table.columns.number'),
      dataIndex: 'phone',
      key: 'phone',
      className: styles.phoneNumberContainer,
      render: (phone: CallRow['phone'], record) => (
        <>
          <Link
            to={`/conversations/${record.id}`}
            onClick={(e) => e.stopPropagation()}
            className={styles.rowLink}
          />
          {phone ? (
            <Flex vertical>
              <Flex align="center">
                {record?.language_flag_url && (
                  <div className={styles.flagContainer}>
                    <img
                      src={record.language_flag_url}
                      alt={record.language || t('table.flagAlt')}
                    />
                  </div>
                )}
                <span className={styles.phoneNumber}>{phone}</span>
                {record.type === 'OutboundCall' && (
                  <TopicChip
                    title={t('table.outbound')}
                    variant="secondary"
                    className={styles.outboundTag}
                  />
                )}
              </Flex>
              {/* The mockup is a single-tenant panel and has no company column. Multi-company
                  admins still need it, so it rides under the number instead. */}
              {showCompany && record.company_name && (
                <span className={styles.companyName}>{record.company_name}</span>
              )}
            </Flex>
          ) : (
            <p className={styles.noRecords}>---</p>
          )}
        </>
      ),
    },
    {
      title: t('table.columns.dateTime'),
      dataIndex: 'created_at',
      key: 'created_at',
      ...sortProps,
      className: styles.dateAndTimeContainer,
      render: (created_at: CallRow['created_at']) => (
        <p className={styles.date}>{formatConversationDateTime(created_at)}</p>
      ),
    },
    {
      title: t('table.columns.topic'),
      dataIndex: 'topics',
      key: 'topics',
      className: styles.topicsContainer,
      // Array.isArray, not a length check: a socket frame that omits topics used to throw
      // here and take the whole table render with it.
      render: (topics: CallRow['topics']) => (
        <Flex className={styles.topicFlexContainer} wrap="wrap">
          {!Array.isArray(topics) || !topics.length ? (
            <p>---</p>
          ) : (
            [...new Set(topics)].map((topic) => (
              <TopicChip
                key={topic}
                className={styles.topicChip}
                title={topic}
                variant={topic === 'Other' ? 'secondary' : 'primary'}
              />
            ))
          )}
        </Flex>
      ),
    },
    {
      title: t('table.columns.summary'),
      dataIndex: 'convo_summary',
      key: 'convo_summary',
      className: styles.summaryContainer,
      render: (summary: CallRow['convo_summary'], record) => (
        <div>
          {!summary ? <p>---</p> : <p className={styles.summaryText}>{summary}</p>}
          {record.sms_summary_message && (
            <>
              <Divider dashed />
              <p className={styles.smsSummaryMessage}>{t('table.smsSummarySent')}</p>
            </>
          )}
        </div>
      ),
    },
    {
      title: t('table.columns.duration'),
      dataIndex: 'duration',
      key: 'duration',
      ...sortProps,
      className: styles.durationContainer,
      render: (_: CallRow['duration'], record) => renderDuration(record),
    },
    {
      title: t('table.columns.rating'),
      dataIndex: 'sms_score',
      key: 'sms_score',
      className: styles.ratingContainer,
      render: (score: CallRow['sms_score']) => <StarRating score={scoreToFive(score, 'scale10')} />,
    },
    {
      title: t('table.columns.status'),
      dataIndex: 'status',
      key: 'status',
      className: styles.statusContainer,
      render: (_: CallRow['status'], record) => (
        <StatusBadge variant={deriveStatusVariant(record)} />
      ),
    },
  ];

  return (
    // AntD's scroll bound pins the table to a width; this wrapper scrolls the container instead.
    <div className={styles.scrollArea}>
      {/* VsTable defaults pagination to false. Rows accumulate behind LoadMoreRows instead. */}
      <VsTable<CallRow>
        onRow={(record) => ({
          onClick: () => navigate(`/conversations/${record.id}`),
          style: { cursor: 'pointer', position: 'relative' },
        })}
        onChange={handleChange}
        className={styles.table}
        dataSource={dataSource}
        columns={columns}
        locale={{
          emptyText: loading ? (
            <div className={styles.emptyState}>
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('table.empty')} />
          ),
        }}
      />
    </div>
  );
};
